import Vue from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import ActionsCard from './ActionsCard/ActionsCard.vue'
import InformerCard from './InformerCard/InformerCard.vue'
import JobDetailsCard from './JobDetailsCard/JobDetailsCard.vue'
import JobOfferDetailsCard from './JobOfferDetailsCard/JobOfferDetailsCard.vue'
import JobStatusBar from './JobStatusBar/JobStatusBar.vue'
import {
  applicationManageMixin,
  archivableMixin,
  disputableMixin,
  jobPartiesMixin,
  offerManageMixin,
  payableMixin,
  refundableMixin,
  reviewableMixin,
  startableMixin,
  waitForRefreshApplicationMixin,
} from './mixins'
import MobileActionsCard from './MobileActionsCard/MobileActionsCard.vue'
import OfferCard from './OfferCard/OfferCard.vue'
import { CHAT, JOB_DETAILS, JOB_DETAILS_ADMIN_APPLICATIONS } from '@/constants/routes'
import responseMixin from '@/mixins/responseMixin'
import rolebleMixin from '@/mixins/rolebleMixin'
import snackMixin from '@/mixins/snackMixin'
import unreadMessagesBlinkTitleMixin from '@/mixins/unreadMessagesBlinkTitleMixin'
import ChatRoom from '@/models-ts/chat/ChatRoom'
import Chat from '@/partials/ChatNew/Chat.vue'
import ChatRoomPrimaryInfo from '@/partials/ChatRoomPrimaryInfo/ChatRoomPrimaryInfo.vue'

export default Vue.extend<any, any, any, any>({
  mixins: [
    applicationManageMixin,
    archivableMixin,
    disputableMixin,
    responseMixin,
    rolebleMixin,
    offerManageMixin,
    jobPartiesMixin,
    snackMixin,
    payableMixin,
    refundableMixin,
    reviewableMixin,
    startableMixin,
    unreadMessagesBlinkTitleMixin,
    waitForRefreshApplicationMixin,
  ],
  components: {
    ActionsCard,
    MobileActionsCard,
    Chat,
    ChatRoomPrimaryInfo,
    JobDetailsCard,
    JobOfferDetailsCard,
    JobStatusBar,
    InformerCard,
    OfferCard,
  },
  data() {
    return {
      initLoaded: false,
      withDetails: false,
      lastUpdatedRoomDate: null,
    }
  },
  computed: {
    ...mapState({
      userIsLoaded: (state: any) => state.user.profile.isLoaded,
      roomId: (state: any) => state.chatNew.openedRoomId,
      initialized: (state: any) => state.chatNew.initialized,
      application: (state: any) => state.jobChat.application.value,
    }),
    ...mapGetters({
      room: 'chatNew/getOpenedRoom',
    }),
    applicationId() {
      const { applicationId } = ChatRoom.parseRoomId(this.roomId)
      return applicationId
    },
    roomUser() {
      if (this.application) {
        return this.isEmployer ? this.application.freelancer : this.application.customer
      }
    },
    roomEntity() {
      if (!this.initLoaded) return
      return {
        name: this.application.job.name,
        link: {
          name: this.isEmployer ? JOB_DETAILS_ADMIN_APPLICATIONS : JOB_DETAILS,
          params: {
            id: this.application.job.id,
            slug: this.application.job.slug,
          },
        },
      }
    },
    backLink() {
      return this.roomEntity?.link
    },
    isTemporary() {
      return (this.room && this.room.temporary) || (this.roomId || '').startsWith('tmp_')
    },
    hasActions() {
      return !this.isTemporary && this.application
    },
    hasOffer() {
      return this.initLoaded && !this.room?.temporary && this.application?.offer && !this.wasStarted
    },
    wasStarted() {
      return this.application?.wasStarted
    },
    applicationIsArchived() {
      return this.isWorker && this.application.meta?.archived
    },
    isMessaged() {
      return this.application.meta?.messaged
    },
  },
  watch: {
    initialized: {
      handler() {
        if (this.initialized) {
          if (!this.roomId) {
            const room = this.$route.query.room
            if (room) {
              this.openRoom(room)
            } else {
              this.$router.replace({ name: CHAT })
            }
          }
        }
      },
      immediate: true,
    },
    roomId: {
      async handler() {
        if (this.roomId) {
          this.initLoaded = false
          if (!this.isTemporary) {
            await this.getApplication(this.applicationId)
            this.initLoaded = true
          } else if (this.room) {
            this.initLoaded = true
          } else {
            this.$router.replace({ name: CHAT })
          }
        }
        if (this.room && this.roomId !== this.$route.query.room) {
          this.$router.replace(this.room.chatLink)
        }
        if (this.$route.query.action) {
          this.processInitialAction(this.$route.query.action)
        }
      },
      immediate: true,
    },
    'room.updatedAt': {
      handler() {
        if (this.lastUpdatedRoomDate && this.application) {
          let dateA = Number(new Date(this.lastUpdatedRoomDate))
          let dateB = Number(new Date(this.room.updatedAt))
          if (Math.abs(dateA - dateB) > 1000) {
            this.waitRefreshApplication()
          }
        }
        this.lastUpdatedRoomDate = this.room?.updatedAt
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    this.closeRoom()
    this.resetJobChatState()
  },
  methods: {
    ...mapMutations({
      resetJobChatState: 'jobChat/resetState',
      updateApplication: 'jobChat/updateApplication',
    }),
    ...mapActions({
      openRoom: 'chatNew/openRoom',
      closeRoom: 'chatNew/closeRoom',
      getApplication: 'jobChat/getApplication',
      setApplicationMeta: 'jobDetails/setApplicationMeta',
    }),
    onOfferEdit() {
      this.editOffer()
    },
    onOfferCancel(id: number) {
      if (this.application.offer?.id === id) {
        this.updateApplication({ id: this.application.id, offer: null })
      }
    },
    async onOfferAccept() {
      this.acceptOffer()
    },
    onDeclineApplicationClick() {
      this.declineApplicationByCustomer()
    },
    onRetractApplicationClick() {
      this.declineApplicationByFreelancer()
    },
    async onRemoveFromArchivedClick() {
      this.setArchivedMeta(false)
    },
    async onMessageSent() {
      const meta = { ...this.application.meta }
      let needUpdate = false
      if (this.isMetaArchived) {
        meta.archived = false
        needUpdate = true
      }
      if (!this.isMessaged) {
        meta.messaged = true
        needUpdate = true
      }

      if (needUpdate) {
        await this.setApplicationMeta({
          jobId: this.application.job.id,
          applicationId: this.application.id,
          meta,
        })
        this.updateApplication({ id: this.application.id, meta })
      }
    },
    processInitialAction(action: string) {
      switch (action) {
        case 'deposit':
          return this.initialStartJob()
        case 'pay':
          return this.initialPayJob()
        case 'dispute':
          return this.initialDispute()
        case 'refund':
          return this.initialRefund()
        case 'feedback':
          return this.initialLeaveFeedback()
      }
    },
    initialStartJob() {
      if (this.hasStartJob) {
        this.startJob()
      }
    },
    initialPayJob() {
      if (this.hasPayJob || this.hasAcceptJob) {
        this.payToTalent()
      }
    },
    initialDispute() {
      if (this.hasCreateDispute) {
        this.createDispute()
      }
    },
    initialRefund() {
      if (this.hasRefund) {
        this.refundToCustomer()
      }
    },
    initialLeaveFeedback() {
      if (this.hasLeaveReview) {
        this.leaveReview()
      }
    },
  },
  metaInfo() {
    return {
      title: this.getMetaTitle(this.application?.job?.name || 'Chat'),
    }
  },
})
